import moment from "moment";
import "moment-timezone";
import { customFormat, DayOfWeekDayShort, DayOfWeekMonthDayShort, DayOfWeekMonthDayYear, DayOfWeekMonthDayYearShort } from "./momentutils.customformats";

type DateTimeLikeObject = moment.Moment | Date | string;

const formatTime = (m: DateTimeLikeObject): string => {
  m = moment.utc(m);
  const timeFormat = site.culture.is24Hr ? "HH:mm" : "hh:mmA";
  return m.format(timeFormat);
};

const formatDaysSince = (m: DateTimeLikeObject): string => {
  m = moment.utc(m);
  const localMoment = moment(site.localNow);
  const daysSince = localMoment.diff(m, "days");

  return  daysSince === 0
    ? RESX.DateTime.Today
    : daysSince === 1
      ? RESX.DateTime.YDay
      : `${daysSince.toString()} ${RESX.DateTime.days}`;
}

const formatOverDue = (days: number) => {
  if (days < -1) return `${RESX.DateTime.lblIn} ${(-days)} ${RESX.DateTime.days}`;
  if (days === -1) return `${RESX.DateTime.lblIn} ${RESX.DateTime.YDay}`;
  if (days === 0) return RESX.DateTime.Today;
  if (days === 1) return RESX.DateTime.YDayOverDue;
  if (days > 1) return RESX.DateTime.XDaysOverDue.replace("{0}", days.toString());
};

// Example format: "Fri, 1"
const formatWrittenDay = (m: moment.Moment) => {
  return customFormat(m, DayOfWeekDayShort);
}

// Example format: Fri, 1 Mar
const formatWrittenDayMonth = (m: moment.Moment) => {
  return customFormat(m, DayOfWeekMonthDayShort);
};

// Example: "Tue"
const formatWeekDay = (m: moment.Moment) => {
  return m.format("ddd");
};

// Example fromat in US: "EEST"
const formatTimeZone = (m: moment.Moment) => {
  return m.format("zz");
}

// Example format in US: "Friday, 2 May 2025 01:00"
const formatLongDateWithTime = (m: DateTimeLikeObject) => {
  m = moment.utc(m);
  return m.format("LLLL");
}

const formatShortDateWithTime = (m: moment.Moment) => {
  return m.format("llll");
}

// Example fromat in US: "Wed, Sep 18, 2024 4:30 PM EEST"
const formatShortDateWithTimeZone = (m: moment.Moment) => {
  return m.tz(systemTimezone).format("llll zz");
}

// Example format in US: "Tue, February 16, 2021"
const formatWrittenDayMonthYear = (d: DateTimeLikeObject) => {
  const m = moment.utc(d);
  return customFormat(m, DayOfWeekMonthDayYear);
};

// Example format in US: "Fri, Mar 21, 2025"
const formatWrittenDayMonthYearShort = (m: moment.Moment) => {
  return customFormat(m, DayOfWeekMonthDayYearShort);
};

// Example format in US: "7/2/2012"
const formatShortDate = (m: DateTimeLikeObject) => {
  m = moment.utc(m);
  return m.format("L");
};

const formatLongDate = (m: DateTimeLikeObject) => {
  m = moment.utc(m);
  return m.format("LL");
}

// Example format in US: "7/2/2012 11:05PM"
const fromatShortDateAndTime = (m: DateTimeLikeObject) => {
  m = moment.utc(m);
  const date = formatShortDate(m);
  const time = formatTime(m);
  return `${date} ${time}`;
};

const formatDayWithDate = (m: moment.Moment) => {
  return `${formatWeekDay(m)} ${formatShortDate(m)}`;
};

const formatWrittenDateTimeUtc = (m: moment.Moment) => {
  return `${fromatShortDateAndTime(m)} UTC`;
};

// like "2 years, 5 days"
const formatTimeDifference = (start: moment.Moment, end: moment.Moment): string => {
  const units: Array<[moment.unitOfTime.Diff, string, (diff: number) => string]> = [
    ["years", RESX.DateTime.YYear, diff => RESX.DateTime.XYears.replace("{0}", diff.toString())],
    ["months", RESX.DateTime.YMonth, diff => RESX.DateTime.XMonths.replace("{0}", diff.toString())],
    ["days", RESX.DateTime.YDay, diff => `${diff} ${RESX.DateTime.days}`], // Manual construction for "days"
    ["hours", RESX.DateTime.YHour, diff => RESX.DateTime.XHours.replace("{0}", diff.toString())],
    ["minutes", RESX.DateTime.YMinute, diff => RESX.DateTime.XMinutes.replace("{0}", diff.toString())],
    ["seconds", RESX.DateTime.YSecond, diff => RESX.DateTime.XSeconds.replace("{0}", diff.toString())]
  ];
  start = start.clone();
  end = end.clone();

  const parts: string[] = [];
  units.forEach(([unit, singular, formatPlural]) => {

    // Exception for months, as we don't want to show months if the difference is less than 100 days
    // So 90 days should be shown as: "90 days"
    // But 110 days should be shown as: "3 months, 20 days"
    if (unit === "months") {
      const differenceInDays = end.diff(start, "days");
      if(differenceInDays > 27 && differenceInDays < 100) {
        const [,, daysUnitPlural] = units.find(u => u[0] === "days");
        parts.push(daysUnitPlural(differenceInDays));
        start = start.add(differenceInDays, "days");
        return;
      }
    }

    const diff = end.diff(start, unit);
    if (diff > 0) {
      start = start.add(diff, unit); // Move the start time forward
      if (diff === 1) {
        parts.push(singular);
      } else {
        parts.push(formatPlural(diff));
      }
    }
  });

  return parts.length > 0 ? parts.join(", ") : RESX.DateTime.XMinutes.replace("{0}", "0");
};

const getNumberOfNights = (start: moment.Moment, end: moment.Moment): number => {
  return end.diff(start, "days");
}

const formatNights = (start: moment.Moment, end: moment.Moment): string => {
  const diff = getNumberOfNights(start, end);

  if(diff === 1) {
    return RESX.Accommodation.Night;
  } else {
    return RESX.Accommodation.Nights.replace("{0}", diff.toString());
  }
}

const parseFromTime = (time: server.dto.Time): moment.Moment => {
  return parseFromTimeString(time.cultureInvariant);
};

const parseFromTimeString = (time: string): moment.Moment => {
  return moment.utc(time, "HH:mm");
};

const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export {
  formatDaysSince,
  formatOverDue,
  formatDayWithDate,
  formatShortDate,
  formatLongDate,
  formatLongDateWithTime,
  formatShortDateWithTime,
  formatTime,
  formatTimeDifference,
  formatWeekDay,
  fromatShortDateAndTime,
  formatWrittenDateTimeUtc,
  formatWrittenDay,
  formatWrittenDayMonth,
  formatWrittenDayMonthYear,
  formatWrittenDayMonthYearShort,
  formatTimeZone,
  formatShortDateWithTimeZone,
  parseFromTime,
  parseFromTimeString,
  formatNights,
  getNumberOfNights,
  systemTimezone
};